// Pantalla «Equipo» — el dueño de un negocio administra a su propia gente.
//
// Antes de esto, dar de alta a alguien era SQL a mano con la llave maestra. Y la
// tabla `miembros` guardaba solo un UUID, así que una lista habría mostrado
// códigos en vez de personas: por eso `0021` agregó `nombre` y `email`.
//
// Dos detalles que parecen cosméticos y no lo son:
//  · Las invitaciones VENCIDAS salen marcadas. La pantalla equivalente de
//    Victorix Work filtra solo por «no aceptada» y nunca mira la fecha: muestra
//    enlaces muertos como si sirvieran, el admin los copia, los manda por
//    WhatsApp y la persona recibe «invitación no disponible». Aquí el dato
//    `vencida` viene ya resuelto desde la base para que no se pueda olvidar.
//  · NO hay botón de «copiar enlace». El token no baja al navegador a propósito
//    (grant por columna, migración 0021 + retorno sin token, 0024). Reenviar
//    manda el correo otra vez y ROTA el vale, que además mata el enlace viejo.
import { useCallback, useEffect, useState } from "react";
import { Aviso, Boton, Chip, SkeletonLista, Vacio } from "./piezas.tsx";
import {
  cambiarRolMiembro, cargarEquipo, explicarError, invitarMiembro, quitarAcceso,
  reenviarInvitacion, revocarInvitacion,
  type InvitacionEquipo, type MiembroEquipo,
} from "./datos.ts";
import type { ContextoPanel } from "./tipos.ts";

const ROLES: Record<string, string> = { dueno: "Dueño", agente: "Asesor" };

function comoFecha(iso: string): string {
  return new Date(iso).toLocaleDateString("es-CO", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * QUÉ PASÓ CON EL CORREO (0033) — dicho como lo diría una persona.
 *
 * Hasta hoy la pantalla solo podía decir «Invitación enviada», porque eso es lo
 * único que sabíamos: que Resend la aceptó para intentarlo. El rebote llega
 * después, por webhook. Sin esta línea, un correo mal escrito deja al dueño
 * esperando a alguien que nunca recibió nada — y a esa persona esperando un
 * correo que nunca va a llegar.
 *
 * Solo se dibuja lo ACCIONABLE: un rebote o una queja. «Entregado» no se
 * anuncia (es lo normal, y llenar la pantalla de verdes hace que el rojo se
 * pierda entre ellos).
 */
export function avisoDeCorreo(
  i: Pick<InvitacionEquipo, "correo_estado" | "correo_detalle">,
): { tono: "alerta"; texto: string; detalle?: string } | null {
  if (i.correo_estado === "rebotado") {
    return {
      tono: "alerta",
      texto: "El correo rebotó",
      detalle: "No le llegó. Revisa que la dirección esté bien escrita y vuelve a invitar. "
        + (i.correo_detalle ?? ""),
    };
  }
  if (i.correo_estado === "queja") {
    return {
      tono: "alerta",
      texto: "Lo marcó como spam",
      detalle: "Esa persona marcó el correo como no deseado. Pásale el acceso por otro medio; "
        + "insistir por correo perjudica el envío a todos los demás.",
    };
  }
  return null;
}

export function Equipo({ ctx }: { ctx: ContextoPanel }) {
  const { sb, tenantId, rol, usuarioId } = ctx;
  const [miembros, setMiembros] = useState<MiembroEquipo[]>([]);
  const [invitaciones, setInvitaciones] = useState<InvitacionEquipo[]>([]);
  const [cargando, setCargando] = useState(true);
  const [correo, setCorreo] = useState("");
  const [rolNuevo, setRolNuevo] = useState<"dueno" | "agente">("agente");
  const [ocupado, setOcupado] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const esDueno = rol === "dueno" || rol === "victorix";

  const recargar = useCallback(async () => {
    try {
      const e = await cargarEquipo(sb, tenantId);
      setMiembros(e.miembros); setInvitaciones(e.invitaciones);
    } catch (e) { setError(explicarError(e)); }
    setCargando(false);
  }, [sb, tenantId]);

  useEffect(() => { void recargar(); }, [recargar]);

  async function hacer(clave: string, fn: () => Promise<unknown>, mensajeOk?: string) {
    setOcupado(clave); setError(null); setOk(null);
    try {
      const r = await fn() as { aviso?: string } | undefined;
      setOk(r?.aviso ?? mensajeOk ?? null);
      await recargar();
    } catch (e) { setError(explicarError(e)); }
    setOcupado(null);
  }

  if (!esDueno) {
    return (
      <div className="p-6">
        <Vacio titulo="Solo el dueño administra el equipo">
          Si necesitas que entre alguien más, pídeselo a quien administra este negocio.
        </Vacio>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-3xl mx-auto space-y-8">
      <header>
        <h1 className="text-[19px] font-semibold tracking-tight">Equipo</h1>
        <p className="text-[13.5px] text-tinta-2 mt-1 leading-relaxed">
          Quién puede entrar a atender el WhatsApp de este negocio.
        </p>
      </header>

      {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}
      {ok && <Aviso tono="ok" onCerrar={() => setOk(null)}>{ok}</Aviso>}

      {/* --- invitar --------------------------------------------------------- */}
      <section className="border border-borde rounded-xl p-4 bg-elevado">
        <h2 className="text-[15px] font-medium">Invitar a alguien</h2>
        <p className="text-[13px] text-tinta-2 mt-1 mb-4 leading-relaxed">
          Le llega un correo con un enlace. Vence en 7 días y solo funciona una vez.
        </p>
        <form
          className="flex flex-col sm:flex-row gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const c = correo.trim();
            void hacer("invitar", async () => {
              const r = await invitarMiembro(sb, tenantId, c, rolNuevo);
              setCorreo("");
              return r;
            }, `Invitación enviada a ${c}.`);
          }}
        >
          <input
            type="email" required placeholder="correo@ejemplo.com" value={correo}
            onChange={(e) => setCorreo(e.target.value)}
            aria-label="Correo de la persona a invitar"
            className="flex-1 h-11 px-3.5 rounded-lg bg-fondo border border-borde text-[15px]
              focus:outline-none focus:border-marca/50"
          />
          <select
            value={rolNuevo} onChange={(e) => setRolNuevo(e.target.value as "dueno" | "agente")}
            aria-label="Rol"
            className="h-11 px-3 rounded-lg bg-fondo border border-borde text-[14px] cursor-pointer"
          >
            <option value="agente">Asesor</option>
            <option value="dueno">Dueño</option>
          </select>
          <Boton type="submit" tono="marca" disabled={ocupado === "invitar" || !correo.trim()}>
            {ocupado === "invitar" ? "Enviando…" : "Invitar"}
          </Boton>
        </form>
        <p className="text-[12px] text-tinta-2 mt-3 leading-relaxed">
          <b className="text-tinta">Asesor</b> atiende conversaciones y contactos.{" "}
          <b className="text-tinta">Dueño</b> además administra el equipo, los ajustes y las campañas.
        </p>
      </section>

      {/* --- invitaciones pendientes ---------------------------------------- */}
      {invitaciones.length > 0 && (
        <section>
          <h2 className="text-[15px] font-medium mb-3">Invitaciones sin aceptar</h2>
          <ul className="space-y-2">
            {invitaciones.map((i) => (
              <li key={i.id}
                  className="flex flex-wrap items-center gap-2 border border-borde rounded-lg px-3 py-2.5">
                <span className="flex-1 min-w-0 text-[14px] truncate">{i.email}</span>
                <Chip>{ROLES[i.rol] ?? i.rol}</Chip>
                {avisoDeCorreo(i) && <Chip tono="alerta">{avisoDeCorreo(i)!.texto}</Chip>}
                {i.vencida
                  ? <Chip tono="alerta">Vencida</Chip>
                  : <span className="text-[12px] text-tinta-2">vence el {comoFecha(i.vence_en)}</span>}
                <Boton tam="sm" tono="neutro" disabled={ocupado === i.id}
                  onClick={() => void hacer(i.id,
                    () => reenviarInvitacion(sb, tenantId, i.email, i.rol),
                    `Le reenviamos el correo a ${i.email}. El enlace anterior dejó de servir.`)}>
                  Reenviar
                </Boton>
                <Boton tam="sm" tono="peligro" disabled={ocupado === i.id}
                  onClick={() => void hacer(i.id, () => revocarInvitacion(sb, i.id), "Invitación cancelada.")}>
                  Cancelar
                </Boton>
                {/* El qué hacer va en renglón aparte y ocupa el ancho: un chip
                    rojo dice que algo pasó, no qué se hace con eso. */}
                {avisoDeCorreo(i) && (
                  <p className="basis-full text-[12.5px] text-tinta-2 leading-snug">
                    {avisoDeCorreo(i)!.detalle}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* --- miembros -------------------------------------------------------- */}
      <section>
        <h2 className="text-[15px] font-medium mb-3">Con acceso</h2>
        {cargando ? <SkeletonLista /> : (
          <ul className="space-y-2">
            {miembros.map((m) => {
              const soyYo = m.user_id === usuarioId;
              return (
                <li key={m.user_id}
                    className={`flex flex-wrap items-center gap-2 border border-borde rounded-lg px-3 py-2.5
                      ${m.activo ? "" : "opacity-55"}`}>
                  <span className="flex-1 min-w-0">
                    <span className="block text-[14px] truncate">
                      {m.nombre ?? m.email ?? "—"}
                      {soyYo && <span className="text-tinta-2 text-[12px]"> · tú</span>}
                    </span>
                    {m.nombre && m.email && (
                      <span className="block text-[12px] text-tinta-2 truncate">{m.email}</span>
                    )}
                  </span>

                  {m.activo ? (
                    <select
                      value={m.rol} disabled={ocupado === m.user_id}
                      aria-label={`Rol de ${m.nombre ?? m.email ?? "este miembro"}`}
                      onChange={(e) => void hacer(m.user_id,
                        () => cambiarRolMiembro(sb, tenantId, m.user_id, e.target.value as "dueno" | "agente"),
                        "Rol actualizado.")}
                      className="h-8 px-2 rounded-lg bg-elevado border border-borde text-[13px] cursor-pointer"
                    >
                      <option value="agente">Asesor</option>
                      <option value="dueno">Dueño</option>
                    </select>
                  ) : <Chip>Sin acceso</Chip>}

                  {m.activo && (
                    <Boton tam="sm" tono="peligro" disabled={ocupado === m.user_id}
                      onClick={() => void hacer(m.user_id,
                        () => quitarAcceso(sb, tenantId, m.user_id), "Le quitamos el acceso.")}>
                      Quitar acceso
                    </Boton>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <p className="text-[12px] text-tinta-2 mt-3 leading-relaxed">
          Quitar el acceso solo cierra la puerta de Chat AI: la persona sigue entrando a las
          demás herramientas de Victorix con la misma cuenta.
        </p>
      </section>
    </div>
  );
}
